import { useMemo } from "react";
import { usd } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDate } from "@/components/DatePicker";
import { OfferPill } from "@/pages/Hiring";

const Row = ({ label, cells, testid }) => (
  <tr className="border-t border-border" data-testid={testid}>
    <th className="p-2 text-left text-xs font-medium text-muted-foreground w-36 align-top">{label}</th>
    {cells.map((c, i) => <td key={i} className="p-2 align-top">{c}</td>)}
  </tr>
);

export function OfferCompare({ open, onOpenChange, offers }) {
  const groups = useMemo(() => {
    const m = {};
    offers.filter((o) => o.status !== "declined").forEach((o) => { (m[o.job_title || "Untitled"] ||= []).push(o); });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [offers]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="offer-compare-modal">
        <DialogHeader><DialogTitle className="font-display">Compare open offers</DialogTitle><DialogDescription>Side-by-side by role. Declined offers are hidden.</DialogDescription></DialogHeader>
        {groups.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="offer-compare-empty">No open offers to compare.</p>}
        <div className="space-y-6">
          {groups.map(([title, list]) => {
            const max = Math.max(...list.map((o) => o.salary || 0));
            return (
              <div key={title} data-testid={`compare-group-${title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-display font-semibold mb-2">{title} <span className="text-xs text-muted-foreground font-normal">({list.length} offer{list.length === 1 ? "" : "s"})</span></div>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50"><tr><th className="p-2 w-36" />{list.map((o) => <th key={o.id} className="p-2 text-left font-semibold">{o.candidate?.name}<div className="text-xs text-muted-foreground font-normal">{o.candidate?.email}</div></th>)}</tr></thead>
                    <tbody>
                      <Row label="Status" cells={list.map((o) => <OfferPill key={o.id} status={o.status} />)} />
                      <Row label="Compensation" testid="compare-pay" cells={list.map((o) => <span key={o.id} className={`font-mono-num ${o.salary === max && list.length > 1 ? "font-bold text-primary" : ""}`}>{usd(o.salary)}{o.pay_type === "hourly" ? "/hr" : "/yr"}</span>)} />
                      <Row label="Start date" cells={list.map((o) => <span key={o.id} className="font-mono-num">{fmtDate(o.start_date)}</span>)} />
                      <Row label="Offer expires" cells={list.map((o) => <span key={o.id} className="font-mono-num">{o.offer_expires ? fmtDate(o.offer_expires) : "—"}</span>)} />
                      <Row label="Reports to" cells={list.map((o) => o.manager || "—")} />
                      <Row label="Benefits" cells={list.map((o) => o.benefits?.length ? <ul key={o.id} className="list-disc pl-4 text-xs space-y-0.5">{o.benefits.map((b, i) => <li key={i}>{b}</li>)}</ul> : <span className="text-muted-foreground text-xs">Standard benefits</span>)} />
                      <Row label="Sent" cells={list.map((o) => <span key={o.id} className="text-xs font-mono-num">{o.sent_at ? fmtDate(o.sent_at.slice(0, 10)) : "Not sent"}</span>)} />
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
